import React from 'react';
import Shield from 'lucide-react/dist/esm/icons/shield.js';
import Lock from 'lucide-react/dist/esm/icons/lock.js';
import CheckCircle from 'lucide-react/dist/esm/icons/check-circle.js';
import Award from 'lucide-react/dist/esm/icons/award.js';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit';
import { useContent } from '../../../contexts/ContentContext';
import { useTranslations } from '../../../contexts/TranslationContext';
import { useSiteConfig } from '../../../contexts/SiteConfigContext';
import { useGeoLocation } from '../../../contexts/GeoLocationContext';
import { getLegalAgeNumber, interpolateLegalAge } from '../../../utils/legalAge';

export default function PurchaseTrustFooterSection({ section, config }) {
  const { c } = useContent();
  const { t: tr } = useTranslations();
  const { siteDetails } = useSiteConfig();
  const { locationData } = useGeoLocation();

  const minAge = getLegalAgeNumber(siteDetails, locationData);
  const defaultDisclaimer = `Must be ${minAge}+ to play. Please play responsibly. For support or to self-exclude, contact our support team.`;
  const disclaimer = interpolateLegalAge(
    c('purchase.trust.disclaimer', tr('purchase.trust.disclaimer') || defaultDisclaimer),
    minAge
  );

  const trustSignals = [
    {
      icon: Shield,
      titleKey: 'purchase.trust.secureLicensedTitle',
      title: 'Secure & Licensed',
      descriptionKey: 'purchase.trust.secureLicensedDesc',
      description: 'Fully licensed and regulated gaming platform',
    },
    {
      icon: Lock,
      titleKey: 'purchase.trust.encryptedPaymentsTitle',
      title: 'Encrypted Payments',
      descriptionKey: 'purchase.trust.encryptedPaymentsDesc',
      description: 'Bank-level encryption protects your information',
    },
    {
      icon: CheckCircle,
      titleKey: 'purchase.trust.fairTransparentTitle',
      title: 'Fair & Transparent',
      descriptionKey: 'purchase.trust.fairTransparentDesc',
      description: 'Independently audited draw processes',
    },
    {
      icon: Award,
      titleKey: 'purchase.trust.guaranteedPayoutsTitle',
      title: 'Guaranteed Payouts',
      descriptionKey: 'purchase.trust.guaranteedPayoutsDesc',
      description: 'All winners paid within 24 hours',
    },
  ];

  return (
    <div className="bg-gray-50 dark:bg-gray-900 rounded-2xl p-6 md:p-8 mt-8">
      <h2 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-6">
        <InlineTranslationEdit
          translationKey="purchase.trust.title"
          value={c('purchase.trust.title', tr('purchase.trust.title') || 'Play with Confidence')}
          defaultText="Play with Confidence"
        />
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {trustSignals.map((signal, idx) => {
          const Icon = signal.icon;
          return (
            <div key={idx} className="flex flex-col items-center text-center">
              <div className="bg-blue-100 dark:bg-blue-900 p-4 rounded-full mb-3">
                <Icon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-1">
                <InlineTranslationEdit
                  translationKey={signal.titleKey}
                  value={c(signal.titleKey, tr(signal.titleKey) || signal.title)}
                  defaultText={signal.title}
                />
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                <InlineTranslationEdit
                  translationKey={signal.descriptionKey}
                  value={c(signal.descriptionKey, tr(signal.descriptionKey) || signal.description)}
                  defaultText={signal.description}
                />
              </p>
            </div>
          );
        })}
      </div>

      <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700 text-center">
        <p className="text-xs text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
          <InlineTranslationEdit
            translationKey="purchase.trust.disclaimer"
            value={disclaimer}
            defaultText={defaultDisclaimer}
          />
        </p>
        {(siteDetails?.formattedLicenses || siteDetails?.licenseNumber) && (
          <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 mt-2">
            {tr('footer.license', 'License:')} {siteDetails.formattedLicenses || `#${siteDetails.licenseNumber}`}
          </p>
        )}
      </div>
    </div>
  );
}
